import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit, Loader2, User } from 'lucide-react';
import { useExpertProfile } from '@/hooks/useExpertProfile';
import { useUpdateExpertProfileStatus } from '@/hooks/useUpdateExpertProfileStatus';

export default function ExpertProfileDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: profileData, isLoading, error } = useExpertProfile(id!);
  const updateStatus = useUpdateExpertProfileStatus();

  useEffect(() => {
    if (profileData?.data) {
      document.title = `${profileData.data.name} | Admin Dashboard`;
    }
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, [profileData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pine-pine" />
      </div>
    );
  }

  if (error || !profileData?.data) {
    return (
      <div className="admin-card p-6">
        <p className="text-red-400">Expert profile not found</p>
        <button
          onClick={() => navigate('/admin/expert-profiles')}
          className="mt-4 text-pine-pine"
        >
          Back to Expert Profiles
        </button>
      </div>
    );
  }

  const profile = profileData.data;

  const handleActivate = () => {
    updateStatus.mutate({ id: id!, status: 'active' });
  };

  const handleDeactivate = () => {
    updateStatus.mutate({ id: id!, status: 'inactive' });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back + Actions Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/expert-profiles')}
          className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Expert Profiles
        </button>
        <Link
          to={`/admin/expert-profiles/${id}/edit`}
          className="admin-button-primary flex items-center gap-2"
        >
          <Edit className="w-4 h-4" />
          Edit Profile
        </Link>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Avatar + Name + Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="admin-card p-6"
          >
            <div className="flex items-center gap-6">
              {profile.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt={profile.name}
                  className="w-24 h-24 rounded-full object-cover border-2 border-pine-line"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-pine-paper flex items-center justify-center border-2 border-pine-line">
                  <User className="w-12 h-12 text-pine-ink-soft" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-pine-ink">{profile.name}</h1>
                <p className="text-pine-ink-soft mt-1">{profile.subtitle}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right column - Metadata & Actions */}
        <div className="space-y-6">
          {/* Details card */}
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-pine-ink">Details</h2>

            <div className="space-y-3">
              <div>
                <span className="text-sm text-pine-ink-soft">Created</span>
                <p className="text-pine-ink">
                  {new Date(profile.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-sm text-pine-ink-soft">Updated</span>
                <p className="text-pine-ink">
                  {new Date(profile.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Status Management card */}
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-pine-ink">Status</h2>

            {/* Current status badge */}
            <div>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold border ${
                  profile.status === 'active'
                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                    : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                }`}
              >
                {profile.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              {profile.status === 'active' ? (
                <button
                  onClick={handleDeactivate}
                  disabled={updateStatus.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-500/30 text-gray-400 hover:bg-gray-500/10 transition-colors"
                >
                  Deactivate Profile
                </button>
              ) : (
                <button
                  onClick={handleActivate}
                  disabled={updateStatus.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                >
                  Activate Profile
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
